import { t } from '@lingui/core/macro';
import { isPresent } from '@luminovo/commons';
import { Box } from '@mui/material';
import { SparklesIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import type { JSX } from 'react';

import { AppLinkOptions, asLinkProps } from '../../routing/links/linkOptions';
import { colorSystem } from '../../theme';
import { BaseButtonProps, SecondaryButton, TertiaryButton, TertiaryIconButton } from '../buttons';
import { Flexbox } from '../Flexbox';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
import { STATUS_ICONS, type StatusIconStatus } from '../StatusIcon/statusIcons';
import { Text } from '../Text';

export type MessageAction = {
    startIcon?: IconElement;
    label: string;
    disabled?: BaseButtonProps['disabled'];
} & ({ onClick: () => void; link?: never } | { link: AppLinkOptions; onClick?: never });

export type MessageProps = {
    variant: 'primary' | 'blue' | 'green' | 'red' | 'yellow';
    attention: 'high' | 'low';
    size: 'small' | 'large';
    title?: string;
    message?: string | JSX.Element;
    action?: MessageAction;
    onClose?: () => void;
    /** Drawn at 20px. Defaults to the variant's icon; `null` shows no icon. */
    icon?: IconElement;
    overrides?: { ActionButton?: React.ComponentType };
};

// Sizes the message icon: it needs no size of its own.
const ICON_SLOT = iconSlot(20);

const VARIANT_STATUS: Record<Exclude<MessageProps['variant'], 'primary'>, StatusIconStatus> = {
    blue: 'info',
    green: 'success',
    red: 'error',
    yellow: 'warning',
};

const DefaultMessageIcon: React.FunctionComponent<Pick<MessageProps, 'variant' | 'attention'>> = ({
    variant,
    attention,
}): JSX.Element => {
    // A shade darker on a high-attention message's tinted background.
    const color = colorSystem[variant][attention === 'high' ? 7 : 6];
    const Glyph = variant === 'primary' ? SparklesIcon : STATUS_ICONS[VARIANT_STATUS[variant]].filled;
    return <Glyph color={color} style={{ alignSelf: 'start' }} />;
};

const MessageContainer: React.FunctionComponent<
    React.PropsWithChildren<Pick<MessageProps, 'variant' | 'attention'>>
> = ({ children, variant, attention }): JSX.Element => {
    const bgcolor = attention === 'high' ? colorSystem[variant][1] : colorSystem.neutral[1];
    const borderColor = attention === 'high' ? colorSystem[variant][3] : colorSystem.neutral[2];

    return (
        <Box
            sx={{
                padding: '14px',
                bgcolor: bgcolor,
                border: `1px solid`,
                borderColor: borderColor,
                borderRadius: '8px',
            }}
        >
            {children}
        </Box>
    );
};

const DefaultActionButton: React.FunctionComponent<Pick<MessageProps, 'size' | 'action'>> = ({
    size,
    action,
}): JSX.Element => {
    if (!isPresent(action)) {
        return <></>;
    }
    const { label, link, onClick, startIcon, disabled } = action;

    switch (size) {
        case 'small':
            return link !== undefined ? (
                <TertiaryButton
                    size="small"
                    style={{ padding: 0 }}
                    startIcon={startIcon}
                    disabled={disabled}
                    {...asLinkProps(link)}
                >
                    {label}
                </TertiaryButton>
            ) : (
                <TertiaryButton
                    size="small"
                    style={{ padding: 0 }}
                    startIcon={startIcon}
                    disabled={disabled}
                    onClick={onClick}
                >
                    {label}
                </TertiaryButton>
            );
        case 'large':
            return link !== undefined ? (
                <SecondaryButton
                    size="medium"
                    style={{ width: 'fit-content' }}
                    startIcon={startIcon}
                    disabled={disabled}
                    {...asLinkProps(link)}
                >
                    {label}
                </SecondaryButton>
            ) : (
                <SecondaryButton
                    size="medium"
                    style={{ width: 'fit-content' }}
                    startIcon={startIcon}
                    disabled={disabled}
                    onClick={onClick}
                >
                    {label}
                </SecondaryButton>
            );
    }
};

const CloseButton: React.FunctionComponent<Pick<MessageProps, 'onClose'>> = ({ onClose }): JSX.Element => {
    if (!isPresent(onClose)) {
        return <></>;
    }

    return (
        <TertiaryIconButton
            size={'small'}
            aria-label={t`Close`}
            style={{ marginLeft: 'auto', padding: '0px' }}
            onClick={onClose}
        >
            <XIcon />
        </TertiaryIconButton>
    );
};

const SmallMessage: React.FunctionComponent<MessageProps> = ({
    variant,
    attention,
    size,
    title,
    message,
    action,
    onClose,
    icon = <DefaultMessageIcon variant={variant} attention={attention} />,
    overrides = {},
}): JSX.Element => {
    const { ActionButton = DefaultActionButton } = overrides;

    return (
        <MessageContainer variant={variant} attention={attention}>
            <Flexbox
                gap={8}
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                }}
            >
                <Flexbox
                    gap={8}
                    sx={{
                        alignItems: 'baseline',
                        ...ICON_SLOT,
                    }}
                >
                    {icon}
                    <Text variant="h4" color={colorSystem.neutral[9]} style={{ whiteSpace: 'nowrap' }}>
                        {title}
                    </Text>
                    <Text variant="body"> {message}</Text>
                </Flexbox>
                <Flexbox gap={4}>
                    <ActionButton size={size} action={action} />
                    <CloseButton onClose={onClose} />
                </Flexbox>
            </Flexbox>
        </MessageContainer>
    );
};

const LargeMessage: React.FunctionComponent<MessageProps> = ({
    variant,
    attention,
    size,
    title,
    message,
    action,
    onClose,
    icon = <DefaultMessageIcon variant={variant} attention={attention} />,
    overrides = {},
}): JSX.Element => {
    const { ActionButton = DefaultActionButton } = overrides;

    return (
        <MessageContainer variant={variant} attention={attention}>
            <Flexbox
                gap={8}
                sx={{
                    alignItems: 'center',
                    ...ICON_SLOT,
                }}
            >
                {icon}
                <Text variant="h4" color={colorSystem.neutral[9]}>
                    {title}
                </Text>
                <CloseButton onClose={onClose} />
            </Flexbox>
            <Flexbox
                gap={24}
                sx={{
                    flexDirection: 'column',
                    paddingLeft: '28px',
                    paddingTop: '6px',
                }}
            >
                <Text variant="body" style={{ paddingRight: '24px' }}>
                    {message}
                </Text>
                <ActionButton size={size} action={action} />
            </Flexbox>
        </MessageContainer>
    );
};

export const Message: React.FunctionComponent<MessageProps> = ({ ...props }): JSX.Element => {
    switch (props.size) {
        case 'small':
            return <SmallMessage {...props} />;
        case 'large':
            return <LargeMessage {...props} />;
    }
};
